import { useState } from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { useClinicConfigContext } from '@/contexts/ClinicConfigContext';
import { SelectionButton } from '@/components/PatientTypeButton';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useClinicServices } from '@/hooks/useClinicData';
import { useOptimizedNavigation } from '@/hooks/useOptimizedNavigation';
import { usePerformanceMonitor } from '@/hooks/usePerformanceMonitor';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Footer } from '@/components/Footer';
import { SvgIcon } from '@/components/SvgIcon';

export function PatientServicePage() {
  const { clinicSlug } = useParams<{ clinicSlug: string }>();
  const { clinic, clinicLoading, clinicError } = useClinicConfigContext();

  // Use React Query for services with caching
  const {
    data: servicesResponse,
    isLoading: servicesLoading,
    error: servicesError,
  } = useClinicServices(clinicSlug || '');

  const services = servicesResponse?.success
    ? servicesResponse.services
    : undefined;
  const [selectedService, setSelectedService] = useState<string | null>(null);

  // Optimized navigation with prefetching
  const { navigateWithPrefetch, handleMouseEnter } = useOptimizedNavigation();

  // Performance monitoring
  const { trackUserInteraction } = usePerformanceMonitor('PatientServicePage');

  // Theme is initialized globally in App via ThemeInitializer

  // Document title
  useDocumentTitle(clinic?.name);

  // Animation variants
  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };
  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };
  const slideInLeft = {
    initial: { opacity: 0, x: -50 },
    animate: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: 'easeOut' },
    },
  };
  const scaleIn = {
    initial: { opacity: 0, scale: 0.9 },
    animate: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };
  const buttonHover = { scale: 1.02, transition: { duration: 0.2 } };
  const buttonTap = { scale: 0.98, transition: { duration: 0.1 } };

  if (clinicLoading || servicesLoading) {
    return (
      <div className='min-h-screen relative'>
        <LoadingSpinner fullScreen size='lg' text='Loading...' />
      </div>
    );
  }

  if (clinicError || servicesError || !clinic) {
    return (
      <div className='min-h-screen relative flex items-center justify-center'>
        <div className='text-center'>
          <h1 className='text-4xl font-bold text-gray-800 mb-4'>404</h1>
          <p className='text-xl text-gray-600 mb-8'>Clinic not found</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className='h-screen w-screen overflow-hidden relative flex flex-col '
      variants={pageVariants}
      initial='initial'
      animate='animate'
      exit='exit'
      transition={{ duration: 0.6 }}
    >
      {/* Header with Logo */}
      <motion.div
        className='flex-shrink-0 p-6'
        variants={slideInLeft}
        initial='initial'
        animate='animate'
      >
        <div className='flex items-center'>
          <motion.div
            className='w-fit'
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
          >
            <img
              src={'/logo.png'}
              alt={`${clinic.name} logo`}
              className='w-full h-full object-contain'
            />
          </motion.div>
        </div>
      </motion.div>

      {/* Main Content */}
      <motion.div
        className='flex-1 flex flex-col justify-center px-6 py-8'
        variants={staggerContainer}
        initial='initial'
        animate='animate'
      >
        <div className='max-w-3xl mx-auto w-full'>
          {/* Welcome Message */}
          <motion.div
            className='text-left mb-12 mt-8 px-8'
            variants={staggerContainer}
            initial='initial'
            animate='animate'
          >
            <motion.h2
              className='text-xl lg:text-3xl font-semibold mb-6'
              style={{ color: 'var(--clinic-text)' }}
              variants={fadeInUp}
              transition={{ delay: 0.3 }}
            >
              Welcome back! It's great to see you again. Let's get you scheduled
              for your next appointment.
            </motion.h2>
            <motion.h3
              className='text-xl lg:text-3xl font-semibold'
              style={{ color: 'var(--clinic-text)' }}
              variants={fadeInUp}
              transition={{ delay: 0.5 }}
            >
              I can help you with the following. What type of appointment would
              you like to book?
            </motion.h3>
          </motion.div>

          {/* Service Selection */}
          <div className='grid md:grid-cols-3 gap-6 mb-8 px-8'>
            {!services || services.length === 0 ? (
              <div className='col-span-3 text-center py-8'>
                <p className='text-gray-500 text-lg'>
                  No services available at the moment.
                </p>
              </div>
            ) : (
              services.map(service => (
                <motion.div
                  key={service.id}
                  variants={scaleIn}
                  whileHover={buttonHover}
                  whileTap={buttonTap}
                  transition={{ duration: 0.2 }}
                >
                  <SelectionButton
                    isSelected={selectedService === service.id}
                    onClick={() => {
                      trackUserInteraction('service_selected', {
                        serviceId: service.id,
                        serviceName: service.name,
                      });
                      setSelectedService(service.id);
                      document.cookie = `selected_service_id=${encodeURIComponent(
                        service.id
                      )}; path=/; max-age=604800`;
                    }}
                    className='w-full aspect-square flex items-center justify-center text-center overflow-hidden'
                  >
                    <div className='flex flex-col items-center space-y-4'>
                      <div className=' rounded-full flex items-center justify-center p-8 bg-white'>
                        {service.icon ? (
                          <SvgIcon
                            svgString={service.icon}
                            alt={service.name}
                            className='w-8 h-8'
                            strokeColor='var(--clinic-icon-stroke)'
                          />
                        ) : (
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='40'
                            height='40'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke={'var(--clinic-icon-stroke)'}
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          >
                            <path d='M9 12l2 2 4-4' />
                            <path d='M21 12c.552 0 1-.448 1-1V5c0-.552-.448-1-1-1H3c-.552 0-1 .448-1 1v6c0 .552.448 1 1 1h18z' />
                            <path d='M3 12v6c0 .552.448 1 1 1h16c.552 0 1-.448 1-1v-6' />
                          </svg>
                        )}
                      </div>
                      <div className='text-center'>
                        <span
                          className={`text-xl font-medium text-center`}
                          style={{ color: 'var(--clinic-text-secondary)' }}
                        >
                          {service.name}
                        </span>
                      </div>
                    </div>
                  </SelectionButton>
                </motion.div>
              ))
            )}
          </div>

          {/* Next Button */}
          <div className='flex justify-center'>
            <motion.button
              disabled={!selectedService}
              onClick={() => {
                if (selectedService) {
                  trackUserInteraction('next_button_clicked', {
                    selectedService,
                  });
                  navigateWithPrefetch(
                    `/${clinicSlug}/provider-select`,
                    clinicSlug || '',
                    'services'
                  );
                }
              }}
              onMouseEnter={() => {
                if (clinicSlug) {
                  handleMouseEnter(clinicSlug, 'services');
                }
              }}
              className='h-[54px] min-w-[300px] px-6 py-3 rounded-xl text-base transition-all duration-300 flex items-center justify-center gap-2.5 shadow-md font-semibold hover:opacity-90'
              style={{
                backgroundColor: 'var(--clinic-secondary)',
                color: 'var(--clinic-text)',
              }}
              whileHover={selectedService ? { scale: 1.02, y: -2 } : {}}
              whileTap={selectedService ? { scale: 0.98 } : {}}
              transition={{ duration: 0.3 }}
            >
              Next
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Footer */}
      <Footer mobileColumnLayout={true} />
    </motion.div>
  );
}
